import { LocalDate, LocalTime } from "@js-joda/core";
import h from "trader-hyperscript";
import { makeEncoderFromCodec, makeRoute } from "trader.ts/route";
import { checkAllCasesHandled } from "trader.ts/utils";
import {
  b2fileid,
  eventid,
  lessonid,
  studentid,
  teacherid,
} from "../../../sql/classy/types";
import * as FA from "../../../styles/fontawesome.css.classes";
import * as style from "../../../styles/style.css.classes";
import { makeClientsideRouter } from "../../client/clientsiderouter";
import {
  closePopupWhenClickingOutsidePopupOnMonthPage,
  renderMonthsWithSelection,
} from "../../client/rendermonth";
import { holiday } from "../../domain/periods";
import {
  bluePlanningColors,
  colors,
  green,
  makeGradient,
  pinkPlanningColors,
  purple,
} from "../../lib/colors";
import { isNil } from "../../lib/istype";
import { HHmm, ddMMMMyyyy, renderFirstNameLastName } from "../../render";
import * as routes from "../../routes";
import { renderClosePopupButton } from "../planning/selectionandforms";
import { stringToJsxClientside } from "../editemailtemplates/client";

type event = {
  id: eventid;
  description: string;
  text: string;
  html: string;
  attachment: b2fileid | null;
  attachmentname: string | null;
  date: LocalDate;
  only_for_admins_and_teachers: boolean;
};

type teacherLessons = {
  tag: "teacher";
  id: teacherid;
  lessons: {
    date: LocalDate;
    count: number;
    lessons_start: LocalTime | null;
    lessons_end: LocalTime | null;
  }[];
};

type studentLessons = {
  tag: "student";
  hasMultipleStudents: boolean;
  lessons: {
    date: LocalDate;
    lessons:
      | {
          id: lessonid;
          time: LocalTime;
          student: {
            id: studentid;
            firstname: string;
          };
          teacher: {
            firstname: string;
            lastname: string;
          };
        }[]
      | null;
  }[];
};

type lessons =
  | {
      tag: "admin";
    }
  | teacherLessons
  | studentLessons;

type mode = null | LocalDate;
const modeC = routes.magicCodec<mode>();
const modeRoute = makeRoute("/selection/{mode:mode}", {
  mode: makeEncoderFromCodec(modeC),
});

const router = makeClientsideRouter(modeRoute, null);

export function render(opts: {
  period: { firstday: LocalDate; lastday: LocalDate };
  events: event[];
  lessons: lessons;
  holidays: holiday[];
}): HTMLElement | HTMLElement[] {
  const selectedS = router.start();
  const months = renderMonthsWithSelection({
    period: opts.period,
    nextPeriod: null,
    holidays: opts.holidays,
    lastday: opts.period.lastday,
    today: LocalDate.now(),
    makeLink: (s: mode) => router.makeHashLink(s),
    saveLinkIntoHistory: false,
    selectedS,
    canSelect: (d) => {
      const hasEvent = !isNil(opts.events.find((ev) => ev.date.isEqual(d)));
      return opts.lessons.tag === "admin"
        ? true
        : opts.lessons.tag === "teacher"
        ? hasEvent ||
          !isNil(opts.lessons.lessons.find((l) => l.date.isEqual(d)))
        : opts.lessons.tag === "student"
        ? hasEvent ||
          !isNil(opts.lessons.lessons.find((l) => l.date.isEqual(d)))
        : checkAllCasesHandled(opts.lessons);
    },
    renderCell: function (d: LocalDate): {
      className: string;
      style: string;
      contents: null;
    } {
      const event = opts.events.find((ev) => ev.date.isEqual(d));
      const hasLesson =
        opts.lessons.tag === "admin"
          ? false
          : opts.lessons.tag === "teacher"
          ? opts.lessons.lessons.find((l) => l.date.isEqual(d))
          : opts.lessons.tag === "student"
          ? opts.lessons.lessons.find((l) => l.date.isEqual(d))
          : checkAllCasesHandled(opts.lessons);
      const colors =
        event && hasLesson
          ? ([bluePlanningColors, getEventColor(event)] as [colors, colors])
          : event
          ? getEventColor(event)
          : hasLesson
          ? bluePlanningColors
          : null;
      const background = colors !== null ? makeGradient(colors) : "";

      const color =
        colors === null
          ? ""
          : Array.isArray(colors)
          ? colors[0].text
          : colors.text;

      const style = `background: ${background}; color: ${color};`;

      return {
        className: "",
        style,
        contents: null,
      };
    },
    renderPopupContents: function (d: LocalDate): HTMLElement {
      const events = opts.events.filter((ev) => ev.date.isEqual(d));

      const lessonsRendered = (() => {
        if (opts.lessons.tag === "admin") {
          return <span></span>;
        } else if (opts.lessons.tag === "teacher") {
          const lessons = opts.lessons.lessons.find((l) => l.date.isEqual(d));
          return isNil(lessons) ? (
            <span></span>
          ) : (
            <a
              href={routes.teacher_lessonrosterRoute.link({
                date: d,
                teacherid: opts.lessons.id,
                mode: "day",
              })}
            >
              {"%n lessen van %start tot %end"
                .replace("%n", lessons.count.toString())
                .replace("%start", lessons.lessons_start?.format(HHmm) || "")
                .replace("%end", lessons.lessons_end?.format(HHmm) || "")}
            </a>
          );
        } else if (opts.lessons.tag === "student") {
          const lessons = opts.lessons.lessons.find((l) => l.date.isEqual(d));
          if (isNil(lessons) || isNil(lessons.lessons)) {
            return <span></span>;
          }

          const hasMultipleStudents = opts.lessons.hasMultipleStudents;
          return lessons.lessons
            .orderBy((l) => l.time.toSecondOfDay())
            .map((l) => {
              return (
                <div>
                  {hasMultipleStudents ? l.student.firstname + ": " : ""}
                  {"Les bij %teacher om %time"
                    .replace("%teacher", renderFirstNameLastName(l.teacher))
                    .replace("%time", l.time.format(HHmm))}
                </div>
              );
            });
        } else {
          return checkAllCasesHandled(opts.lessons);
        }
      })();

      const eventsRendered = events.map((ev) => (
        <div className={style.panel} style="margin: -8px; margin-bottom: 16px;">
          <div
            className={style.panel_header}
            style={`background: ${makeGradient(
              getEventColor(ev),
            )}; color: white;`}
          >
            {ev.description}
          </div>
          <div style=" white-space: pre-wrap;">
            {
              Array.from(
                stringToJsxClientside("<div>" + ev.html + "</div>").childNodes,
              ) as HTMLElement[]
            }
          </div>
          <div style="display: flex; justify-content: flex-end;">
            {ev.attachment && ev.attachmentname ? (
              <a
                href={routes.downloadEventAttachmentSpec.route.link({
                  eventid: ev.id,
                })}
                download={ev.attachmentname}
                className={style.text_with_icon + style.button_small}
                target="_blank"
                style="margin-right: 8px;"
              >
                <i className={FA.icon_paperclip_solid}></i>
                <div>{"Download bijlage"}</div>
              </a>
            ) : (
              <span></span>
            )}
            {opts.lessons.tag === "admin" ? (
              <a
                className={style.button_small}
                href={routes.editeventRoute.link({ eventid: ev.id })}
              >
                {"Pas aan"}
              </a>
            ) : (
              <span></span>
            )}
          </div>
        </div>
      ));

      return (
        <div
          onclick={(ev) => ev.stopPropagation()}
          className={style.panel + style.panel_as_popup}
        >
          <div
            className={style.panel_header}
            style={`background-color: var(--accent-100); border: 2px solid var(--accent-500); color: var(--accent-900);`}
          >
            {renderClosePopupButton(
              () => (document.location.hash = router.makeHashLink(null)),
            )}
            {ddMMMMyyyy(d)}
          </div>

          {lessonsRendered}

          {eventsRendered}

          {opts.lessons.tag === "admin" ? (
            <div style="text-align: right">
              <a
                className={style.button_small}
                href={routes.neweventRoute.link({ date: d })}
              >
                {"Plan nieuw evenement"}
              </a>
            </div>
          ) : (
            <span></span>
          )}
        </div>
      );
    },
  });

  return (
    <main
      onclick={(ev) =>
        closePopupWhenClickingOutsidePopupOnMonthPage(
          router.makeHashLink(null),
          ev,
          { saveLinkIntoHistory: false },
        )
      }
    >
      {months}
    </main>
  );
}

function getEventColor(ev: event): colors {
  return ev.only_for_admins_and_teachers ? pinkPlanningColors : purple;
}
